import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight, Fire, PuzzlePiece, Star, Trophy } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DIFFICULTY, dayLabel, starText } from "@/lib/daily";

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

function Stat({ icon, value, label }) {
  return (
    <div className="flex-1 bg-surface border border-border rounded-2xl p-4 text-center">
      <div className="font-display text-3xl font-black flex items-center justify-center gap-1.5">{icon}{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

// Every daily puzzle so far: a month calendar to pick one, your stats, and a
// list of the days with your result. Any day can be played (or retried for
// stars if you haven't finished it yet).
export default function DailyArchive() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [month, setMonth] = useState(null); // "2026-10"

  useEffect(() => {
    api.get("/daily")
      .then((r) => { setData(r.data); setMonth(r.data.days[0]?.date.slice(0, 7)); })
      .catch((e) => setError(formatApiError(e) || "Couldn't load the puzzles"));
  }, []);

  const byDate = useMemo(() => Object.fromEntries((data?.days || []).map((d) => [d.date, d])), [data]);
  const months = useMemo(() => [...new Set((data?.days || []).map((d) => d.date.slice(0, 7)))], [data]);

  if (error) return <main className="max-w-3xl mx-auto px-6 py-14 text-muted-foreground">{error}</main>;
  if (!data) return <main className="max-w-3xl mx-auto px-6 py-14" />;

  const today = data.days[0];
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7; // Monday first
  const daysIn = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const mi = months.indexOf(month);
  const inMonth = data.days.filter((d) => d.date.startsWith(month));

  return (
    <main className="max-w-3xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid="daily-archive">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Daily Puzzles</h1>
          <p className="text-sm text-muted-foreground mt-1">Win in one turn. A new one every day; play any day you missed. Stars show how hard each one is: ★ easy, ★★ medium, ★★★ hard.</p>
        </div>
        <Button asChild className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground flex-none" data-testid="archive-today">
          <Link to="/daily">{today?.finished ? "Today's result" : "Play today's"}</Link>
        </Button>
      </div>

      <div className="flex gap-3 mb-8">
        <Stat value={data.solved} label="Solved" icon={<Trophy size={22} weight="duotone" className="text-brand" />} />
        <Stat value={data.streak} label={`Streak · best ${data.best_streak}`} icon={<Fire size={22} weight="fill" className="text-orange-400" />} />
        <Stat value={data.stars} label="Stars earned" icon={<Star size={22} weight="fill" className="text-amber-400" />} />
      </div>

      <div className="bg-surface border border-border rounded-2xl p-4 md:p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="icon" className="rounded-full" disabled={mi >= months.length - 1}
            onClick={() => setMonth(months[mi + 1])} aria-label="Earlier month">
            <CaretLeft size={18} weight="bold" />
          </Button>
          <div className="font-display font-extrabold text-lg">
            {first.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" })}
          </div>
          <Button variant="ghost" size="icon" className="rounded-full" disabled={mi <= 0}
            onClick={() => setMonth(months[mi - 1])} aria-label="Later month">
            <CaretRight size={18} weight="bold" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 md:gap-2">
          {WEEKDAYS.map((w, k) => (
            <div key={k} className="text-[11px] font-bold text-muted-foreground text-center">{w}</div>
          ))}
          {Array.from({ length: lead }, (_, k) => <div key={`x${k}`} />)}
          {Array.from({ length: daysIn }, (_, k) => {
            const iso = `${month}-${String(k + 1).padStart(2, "0")}`;
            const d = byDate[iso];
            const base = "h-12 md:h-14 rounded-xl border flex flex-col items-center justify-center font-display font-bold text-sm";
            if (!d) return <div key={iso} className={`${base} border-border/40 text-muted-foreground/40`}>{k + 1}</div>;
            const cls = d.won
              ? "bg-emerald-500/15 border-emerald-500/40"
              : d.finished
              ? "bg-destructive/10 border-destructive/30"
              : "bg-background border-border hover:border-foreground/40";
            return (
              <Link key={iso} to={`/daily/${d.n}`} className={`${base} ${cls} ${d.n === data.today ? "ring-2 ring-brand" : ""}`}
                data-testid={`archive-day-${d.n}`}>
                {k + 1}
                <span
                  className={`text-[10px] leading-none mt-0.5 tracking-tight ${d.won ? "text-amber-400" : d.finished ? "text-destructive" : "text-muted-foreground"}`}
                  title={`${DIFFICULTY[d.difficulty]}${d.won ? ", solved" : d.finished ? ", not solved" : ""}`}
                >
                  {d.finished && !d.won ? "✕" : "★".repeat(d.difficulty || 1)}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      <ul className="space-y-2">
        {inMonth.map((d) => (
          <li key={d.n}>
            <Link to={`/daily/${d.n}`} className="flex items-center gap-3 bg-surface border border-border rounded-xl px-4 py-3 hover:border-foreground/40 transition-colors">
              <PuzzlePiece size={20} weight="duotone" className="text-brand flex-none" />
              <div className="min-w-0">
                <div className="font-display font-bold">
                  #{d.n} · {d.n === data.today ? "Today" : dayLabel(d.date)}
                  <span className="ml-2 text-amber-400 tracking-wider text-sm" title={DIFFICULTY[d.difficulty]}>{starText(d.difficulty)}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {d.won
                    ? `Solved on try ${d.tries} · ${d.tiles} tiles (par ${d.par})`
                    : d.finished
                    ? "Not solved"
                    : d.tries
                    ? `${3 - d.tries} ${3 - d.tries === 1 ? "try" : "tries"} left`
                    : `${DIFFICULTY[d.difficulty]} · par ${d.par} tiles`}
                </div>
              </div>
              <div className={`ml-auto font-display font-extrabold text-sm ${d.won ? "text-emerald-500" : "text-brand"}`}>
                {d.won ? "Solved" : d.finished ? "Solution" : "Play"}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </main>
  );
}
